import { DoqType, ResponsiveDoqWithSpeechBubble } from '@ardoq/doq';
import { EmptyStatesDoqContainer } from '@ardoq/graph';
import { FlexBox, Stack } from '@ardoq/layout';
import { t, type I18nLocale, type WithI18nLocale } from '@ardoq/i18n';
import { NoNumericFieldsDoq } from 'tabview/graphViews/emptyStates/NoNumericFieldsDoq';
import type { Surface3DEmptyReason } from './sourceData/buildSurfaceSourceData';

type EmptyStateDoqProps = WithI18nLocale;

/**
 * Nothing picked yet, which is the state every user meets the first time they open the view.
 *
 * Its own message rather than the timeline's `NoDateRangeTimeFieldsSelectedDoq`, which was what it
 * borrowed. That one says "all date range or time fields are currently hidden" and asks for a date
 * or time field - wrong twice over on a chart that takes only numeric fields and is hiding nothing.
 */
const NoFieldsSelectedDoq = ({ i18nLocale }: EmptyStateDoqProps) => (
  <EmptyStatesDoqContainer>
    <ResponsiveDoqWithSpeechBubble
      doqType={DoqType.BUBBLE_CHART_ERROR}
      title={t(
        i18nLocale,
        'views:surface3d.emptyState.noFieldsSelected.title',
        'Choose the fields to plot'
      )}
      message={
        <Stack gap="xlarge">
          <FlexBox gap="medium">
            {t(
              i18nLocale,
              'views:surface3d.emptyState.noFieldsSelected.message1',
              'This view reads three numeric or list fields: one for the X-axis, one for the Y-axis, and one for height.'
            )}
          </FlexBox>
          <FlexBox gap="medium">
            {t(
              i18nLocale,
              'views:surface3d.emptyState.noFieldsSelected.message2',
              'Pick a field for each in the settings bar above.'
            )}
          </FlexBox>
        </Stack>
      }
    />
  </EmptyStatesDoqContainer>
);

/**
 * Positions that enclose no area, which no existing empty state covers.
 *
 * Its own message because its remedy is specific and non-obvious: the data is fine and the
 * fields are chosen, but a surface is interpolated between positions and these give it nothing
 * to interpolate across. Two ways in, and the copy has to cover both - fewer than three
 * positions, and any number of them on a single line.
 */
export const NoAreaToInterpolateDoq = ({ i18nLocale }: EmptyStateDoqProps) => (
  <EmptyStatesDoqContainer>
    <ResponsiveDoqWithSpeechBubble
      doqType={DoqType.EMPTY_BOX}
      title={t(
        i18nLocale,
        'views:surface3d.emptyState.noAreaToInterpolate.title',
        'These components do not spread far enough for a surface'
      )}
      message={
        <Stack gap="xlarge">
          <FlexBox gap="medium">
            {t(
              i18nLocale,
              'views:surface3d.emptyState.noAreaToInterpolate.message1',
              'A surface is interpolated between the positions your components occupy. These do not enclose an area, so there is nothing to interpolate across.'
            )}
          </FlexBox>
          <FlexBox gap="medium">
            {t(
              i18nLocale,
              'views:surface3d.emptyState.noAreaToInterpolate.message2',
              'Choose fields that spread your components across the plane.'
            )}
          </FlexBox>
        </Stack>
      }
    />
  </EmptyStatesDoqContainer>
);

/** Which axis lost the most components, and how many, for the copy to name. */
export type WidestGap = { axisTitle: string; count: number };

type NoPlottableComponentsDoqProps = EmptyStateDoqProps & {
  excludedCount: number;
  widestGap: WidestGap | null;
};

/**
 * Not one component carried a value for all three fields.
 *
 * Its own message because "these share fewer than three positions" is false here - they share
 * none - and its remedy, spreading the components across the plane, is not available to someone
 * whose components have no value on the chosen fields at all. `getFieldsForViewpoint` offers
 * every numeric field on any plotted component type, so on a traversal spanning two component
 * types a user can pick three fields no single component carries and land here immediately.
 *
 * The counts are what make it actionable: naming the field that excluded the most says which
 * pick to change, where a general instruction cannot.
 */
const NoPlottableComponentsDoq = ({
  i18nLocale,
  excludedCount,
  widestGap,
}: NoPlottableComponentsDoqProps) => (
  <EmptyStatesDoqContainer>
    <ResponsiveDoqWithSpeechBubble
      doqType={DoqType.EMPTY_BOX}
      title={t(
        i18nLocale,
        'views:surface3d.emptyState.noPlottableComponents.title',
        'No components have a value for all three fields'
      )}
      message={
        <Stack gap="xlarge">
          <FlexBox gap="medium">
            {t(
              i18nLocale,
              'views:surface3d.emptyState.noPlottableComponents.message1',
              { count: excludedCount },
              'A component is placed by its values, so it needs one for each of the three fields. None of the {{count}} in view does.'
            )}
          </FlexBox>
          <FlexBox gap="medium">
            {widestGap
              ? t(
                  i18nLocale,
                  'views:surface3d.emptyState.noPlottableComponents.message2Field',
                  { count: widestGap.count, field: widestGap.axisTitle },
                  '{{count}} of them have no value for {{field}}. Try another field there, or a traversal whose components share one.'
                )
              : t(
                  i18nLocale,
                  'views:surface3d.emptyState.noPlottableComponents.message2',
                  'Try other fields, or a traversal whose components share the ones you picked.'
                )}
          </FlexBox>
        </Stack>
      }
    />
  </EmptyStatesDoqContainer>
);

/**
 * What the studio's overlay stands on when its dispatch does not arrive.
 *
 * `updateViewEmptyStateData$` debounces and then dedupes the payload with `isEqual`, while
 * `handleMainViewChanged` resets the reduced state on every view change - so leaving this view
 * for one that publishes nothing of its own and coming back re-dispatches a payload identical to
 * the last one through, which the dedupe drops, leaving `isViewEmpty` false.
 *
 * Says nothing about a remedy. Whichever of the three causes it is, the overlay names it and
 * offers Edit dataset; this only has to keep the pane from going blank, and a guess at the cause
 * would be wrong two times in three.
 */
const NoComponentsInViewDoq = ({ i18nLocale }: EmptyStateDoqProps) => (
  <EmptyStatesDoqContainer>
    <ResponsiveDoqWithSpeechBubble
      doqType={DoqType.EMPTY_BOX}
      title={t(
        i18nLocale,
        'views:surface3d.emptyState.noComponentsInView.title',
        'There is nothing in view to plot'
      )}
      message={
        <FlexBox gap="medium">
          {t(
            i18nLocale,
            'views:surface3d.emptyState.noComponentsInView.message',
            'No components are in view, so there are no positions to build a surface from.'
          )}
        </FlexBox>
      }
    />
  </EmptyStatesDoqContainer>
);

export type Surface3DEmptyStateProps = {
  emptyReason: Surface3DEmptyReason;
  excludedCount: number;
  widestGap: WidestGap | null;
  i18nLocale: I18nLocale;
};

/**
 * One message per reason, because each has a different remedy.
 *
 * A single "nothing to show" leaves a user changing fields at random when the real problem is
 * that the workspace has no numeric fields at all, or that every component sits on the same
 * two positions.
 */
export const Surface3DEmptyState = ({
  emptyReason,
  excludedCount,
  widestGap,
  i18nLocale,
}: Surface3DEmptyStateProps) => {
  if (emptyReason === 'no numeric fields') {
    return <NoNumericFieldsDoq doqType={DoqType.BUBBLE_CHART_ERROR} />;
  }

  if (emptyReason === 'no plottable components') {
    return (
      <NoPlottableComponentsDoq
        i18nLocale={i18nLocale}
        excludedCount={excludedCount}
        widestGap={widestGap}
      />
    );
  }

  if (emptyReason === 'no area to interpolate') {
    return <NoAreaToInterpolateDoq i18nLocale={i18nLocale} />;
  }

  /* Answered by the studio rather than here, and backed by a doq of our own for when it is not.
     An empty `scopeComponentIds` in viewpoint mode has three causes - the dataset is empty, a
     filter or a hidden loaded state removed everything, or a required path matched nothing - and
     `buildEmptyStateLayoutBasedOnDataset` tells them apart and offers Edit dataset.
     `NoDataAvailableDoq` collapsed all three into "add data through the Grid Editor", which for
     the filtered case is the one remedy guaranteed not to work: the components exist.

     The two do not draw twice. `EmptyStateLayout` is `position: absolute; inset: 0` over an
     opaque `surfaceDefault`, so the studio's answer covers this one wherever it arrives. */
  if (emptyReason === 'no components') {
    return <NoComponentsInViewDoq i18nLocale={i18nLocale} />;
  }

  /* Position or height still unpicked. Nothing is preselected, because guessing which numeric
     fields mean something produces a confident landscape out of three arbitrary columns - so this
     is the first thing every user sees, rather than an edge case. */
  return <NoFieldsSelectedDoq i18nLocale={i18nLocale} />;
};
